import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { ApiService } from '@/_services/api.service';
import { captureStreamingStateAtom, endStreamingAtom, isStreamingAtom } from '../atoms/streamingAtoms';
import { abortCurrentStream } from '../streaming/useStreamingMessage';
import { queryKeys } from '../queries/queryKeys';
import { AgentActivity, EMessageRole, type IMessage } from '@/types';

/**
 * Mutation hook to stop an in-progress AI response generation.
 *
 * Flow:
 * 1. Capture current Jotai streaming state (partial text, activities, etc.)
 * 2. Abort the SSE stream (stops client-side consumption)
 * 3. Build stopped message locally and commit to TanStack cache (optimistic)
 * 4. POST to /conversations/[id]/messages/stop to persist + release lock
 * 5. Replace optimistic message with server response
 */
export const useStopGeneration = () => {
	const streamingState = useAtomValue(captureStreamingStateAtom);
	const isStreaming = useAtomValue(isStreamingAtom);
	const endStreaming = useSetAtom(endStreamingAtom);
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!isStreaming || !streamingState.conversationId) {
				throw new Error('No active stream to stop');
			}

			// 1. Capture state BEFORE aborting (abort resets Jotai)
			const captured = { ...streamingState };
			const conversationId = captured.conversationId!;

			// 2. Abort the SSE stream (stops client-side event consumption)
			abortCurrentStream();
			endStreaming();

			// 3. Build local stopped message for immediate cache update
			const stoppedActivity: AgentActivity = {
				type: 'agent_activity',
				category: 'stopped',
				title: 'Generation Stopped',
				content: 'Response generation was stopped by the user.',
				timestamp: new Date()
			};

			const localMessageId = `stopped-${Date.now()}`;
			const localMessage: IMessage = {
				id: localMessageId,
				conversationId,
				text: captured.messageText || '*Response generation was stopped by the user.*',
				role: EMessageRole.ASSISTANT,
				createdAt: new Date(),
				updatedAt: new Date(),
				agentActivities: [...captured.activities, stoppedActivity],
				citations: captured.citations,
				tokenUsage: captured.tokenUsage
			};

			// 4. Commit optimistic stopped message to TanStack cache immediately
			queryClient.setQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId), (old = []) => [
				...old,
				localMessage
			]);

			// 5. Persist to server — returns ALL messages (human + stopped assistant)
			const response = await ApiService.StopGeneration({
				conversationId,
				partialText: captured.messageText,
				activities: captured.activities,
				tokenUsage: captured.tokenUsage
			});

			// 6. Replace entire cache with server's full message list
			if (response.data) {
				queryClient.setQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId), response.data);
			}

			return response;
		}
	});
};
